import api from './api'
import { LoginRequest, LoginResponse, User, Theme } from './types'

export const authService = {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login/', credentials)
    const { access, refresh } = response.data

    // Store tokens
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)

    return response.data
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        await api.post('/auth/logout/', { refresh: refreshToken })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear auth tokens but preserve user preferences (remember me, sidebar state)
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    }
  },

  /**
   * Get current user profile
   */
  async me(): Promise<User> {
    const response = await api.get<User>('/auth/me/')
    return response.data
  },

  /**
   * Update user theme
   */
  async updateTheme(theme: Theme): Promise<User> {
    const response = await api.patch<User>('/auth/me/', { theme })
    return response.data
  },

  /**
   * Change user password
   */
  async changePassword(data: { old_password: string; new_password: string }): Promise<void> {
    await api.post('/auth/change-password/', data)
  },

  /**
   * Update user profile (email, first_name, last_name)
   */
  async updateProfile(data: { email?: string; first_name?: string; last_name?: string }): Promise<User> {
    const response = await api.patch<User>('/auth/me/', data)
    return response.data
  },

  /**
   * Get all active users (for admin forms)
   */
  async getUsers(): Promise<{ id: number; username: string; email: string; first_name: string; last_name: string }[]> {
    const response = await api.get('/auth/users/')
    return response.data
  },

  /**
   * Request password reset email
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/password-reset/', { email })
    return response.data
  },

  /**
   * Verify password reset code
   */
  async verifyResetCode(data: { email: string; code: string }): Promise<{ valid: boolean }> {
    const response = await api.post<{ valid: boolean }>('/auth/password-reset/verify-code/', data)
    return response.data
  },

  /**
   * Confirm password reset with 6-digit code
   */
  async confirmPasswordReset(data: { email: string; code: string; new_password: string }): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/password-reset/confirm/', data)
    return response.data
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token')
  },
}

export default authService
